/**
 * Unread-count badge: the OS icon pill, and the number the tab title carries.
 *
 * A desktop notification is transient — it fades after a few seconds, and one
 * that arrives while the machine is locked may never be seen at all. The badge
 * is the part that persists: it answers "did anything happen while I was away?"
 * without opening the app.
 *
 * Two surfaces, deliberately:
 *
 *  - **Tab title** — `(3) Proporio · Cockpit`. Works everywhere, needs no
 *    permission. The title itself is written by {@link ProporioTitleStrategy},
 *    the app's single title writer; this service only exposes the count, and
 *    the strategy composes the two. A second writer would fight the router and
 *    lose on every navigation.
 *  - **Badging API** — `navigator.setAppBadge()`, the dot on the installed PWA
 *    / taskbar icon. Unsupported on plenty of browsers, hence the capability
 *    guard; there is no fallback and none is needed, since the tab title
 *    already covers the same need.
 *
 * The count comes from the notifications store, which is already live over
 * `users/{uid}/notifications/data` — no extra read, and it updates in the same
 * tick as the bell.
 */

import { Injectable, Injector, effect, inject } from '@angular/core';

import { LoggerService } from '@core/services/infra/logger.service';
import { NOTIFICATIONS_STORE } from '@shared/definitions/stores/notifications-store.token';

/** The slice of `navigator` this service uses; both members are optional. */
interface BadgingNavigator {
  setAppBadge?: (count?: number) => Promise<void>;
  clearAppBadge?: () => Promise<void>;
}

@Injectable({ providedIn: 'root' })
export class AppBadgeService {
  // Lazy via `Injector`, not `inject(NOTIFICATIONS_STORE)` directly: this
  // service is injected by `ProporioTitleStrategy`, which the Router can
  // construct as early as the FIRST navigation — itself sometimes still
  // inside the app's root bootstrap. `NOTIFICATIONS_STORE`'s Firestore
  // implementation transitively needs `AuthService`, and `AuthService`
  // needs `Router` to construct — an eager `inject(NOTIFICATIONS_STORE)`
  // here closed that loop into an NG0200 circular dependency at bootstrap.
  // Resolved only when the count is actually read, after both exist —
  // same pattern as `FirestoreApi`/`AuthStore` (issue #484).
  private readonly injector = inject(Injector);
  private readonly logger = inject(LoggerService);

  /**
   * Unread notifications. Read by {@link ProporioTitleStrategy} to prefix the
   * tab title, so it is exposed rather than kept private.
   */
  unreadCount(): number {
    return this.injector.get(NOTIFICATIONS_STORE).unreadCount();
  }

  constructor() {
    effect(() => this.applyOsBadge(this.unreadCount()));
  }

  /**
   * Push the count to the OS icon.
   *
   * Failures are swallowed on purpose: the API rejects for reasons entirely
   * outside the app's control (the PWA is not installed, the user revoked the
   * permission, the platform lies about support). A badge that cannot be drawn
   * is not a problem worth surfacing — the tab title still carries the number.
   */
  private applyOsBadge(count: number): void {
    if (typeof navigator === 'undefined') return;
    const nav = navigator as Navigator & BadgingNavigator;

    try {
      if (count > 0) {
        void nav.setAppBadge?.(count)?.catch(() => undefined);
      } else {
        void nav.clearAppBadge?.()?.catch(() => undefined);
      }
    } catch (err) {
      this.logger.debug(`[AppBadgeService] badge unavailable: ${String(err)}`);
    }
  }
}

/**
 * Prefix a page title with an unread count.
 *
 * Pure so the title strategy stays trivially testable, and so the "what does
 * zero look like" question has exactly one answer: no prefix at all, not
 * `(0)`. Counts above 99 read as `99+` — the exact number stops carrying
 * information long before that, and a four-character prefix pushes the actual
 * page name out of a narrow tab.
 */
export function withUnreadPrefix(title: string, count: number): string {
  if (!Number.isFinite(count) || count <= 0) return title;
  return `(${count > 99 ? '99+' : count}) ${title}`;
}
